import React from 'react';
import {
  Box,
  Grid,
  MenuItem,
  SvgIcon,
  Typography,
  IconButton,
  Menu,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { InfoCard, SparkButton } from 'sharedComponents/UI';
import { PlusIcon } from 'components/SVG';
import BoardListViewSkeleton from 'sharedComponents/Skeleton/BoardListViewSkeleton';
import { useBoards } from 'hooks/Board/useBoards';
import { useAddBoards } from 'hooks/Board';
import BoardsListView from './BoardsListView';
import { PaginationQuery } from 'utilities/hooks/urlGeneration';
import { usePopover } from 'hooks/common';

import SparkMainAppSearchFilters from 'components/common/SparkMainAppSearchFilters/V2';
import { useSparkSearchContext } from 'hooks/common/useSparkSearchContextV2';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const boardsFilterDropdown = [
  {
    value: 'workspace',
    label: 'Workspace',
  },
  {
    value: 'my-boards',
    label: 'My Boards',
  },
  {
    value: 'shared',
    label: 'Shared with me',
  },
];

const PAGINATION_OPTIONS = { pageNumber: 1, pageSize: 5 };

function BoardsScreen() {
  const { filter, handleItemTypeChange, handleQueryChange } =
    useSparkSearchContext();
  const { open, openPopover, closePopover, anchorEl } = usePopover();

  const [currentPagination, setCurrentPagination] =
    React.useState<PaginationQuery>(PAGINATION_OPTIONS);

  const {
    isLoading: isBoardsLoading,
    boards: boardsData,
    isFetching: isBoardsFetching,
    total: totalBoards,
  } = useBoards({
    filters: filter,
    pagination: currentPagination,
  });

  React.useEffect(() => {
    setCurrentPagination(PAGINATION_OPTIONS);
  }, [filter]);

  const navigate = useNavigate();
  const { mutateAsync: addBoard } = useAddBoards();

  const handleAddBoard = async () => {
    const response = await addBoard({ name: 'Untitled' });
    const id = response.data.id;
    localStorage.setItem(
      'boardView',
      JSON.stringify({ view: 'canvas', boardID: id })
    );
    navigate(`/boards/${id}`);
  };
  return (
    <Grid container justifyContent='center' component='div'>
      <Box display={'flex'} justifyContent='space-between' width={'100%'}>
        <Grid>
          <Typography variant='h4' sx={{ fontFamily: 'Mont-semibold' }}>
            Boards
          </Typography>
        </Grid>
        <SparkButton
          variant='contained'
          startIcon={
            <SvgIcon
              sx={{
                '& path': {
                  fill: 'white',
                },
                stroke: 'white',
              }}
            >
              <PlusIcon color='white' width='22' height='22' />
            </SvgIcon>
          }
          onClick={handleAddBoard}
        >
          <Typography
            sx={{
              fontSize: '18px',
              textTransform: 'capitalize',
            }}
          >
            Create Board
          </Typography>
        </SparkButton>
      </Box>

      <Grid item alignItems='center' xs={12}>
        <Box
          sx={{
            maxHeight: 500,
            mt: 0,
            backgroundColor: '#F9F9F9',
            boxShadow: 'none',
          }}
        >
          <Grid display={'flex'} gap={2} width={'100%'} marginY={3}>
            <SparkMainAppSearchFilters
              shouldHideAllFilters={true}
              placeholder='Search Boards'
              containerStyles={{ marginTop: 0 }}
              textFieldContainerStyles={{ marginTop: 0 }}
              customEndAdornment={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {!!filter.search?.length && (
                    <IconButton
                      onClick={() => {
                        handleQueryChange('');
                      }}
                      size='small'
                    >
                      <CloseIcon fontSize='small' />
                    </IconButton>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      padding: '0 0 0 10px',
                      borderLeft: filter.search?.length
                        ? '1px solid #EEEEEE'
                        : 'none',
                      height: '30px',
                      marginRight: '0',
                    }}
                  >
                    <Box
                      onClick={openPopover}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 6px',
                        borderRadius: '4px',
                        transition: 'all 0.2s',
                        maxWidth: '150px',
                        backgroundColor: (theme) => theme.palette.primary.light,
                        '&:hover': {
                          backgroundColor: (theme) =>
                            `${theme.palette.primary.light}CC`,
                        },
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: (theme) => theme.palette.primary.main,
                          letterSpacing: '0.01em',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '120px',
                        }}
                      >
                        {boardsFilterDropdown?.find(
                          (item) => item.value === filter.selectedItemType
                        )?.label || boardsFilterDropdown[0].label}
                      </Typography>
                      <ExpandMoreIcon
                        fontSize='small'
                        sx={{
                          color: (theme) => theme.palette.primary.main,
                          width: '18px',
                          height: '18px',
                          flexShrink: 0,
                        }}
                      />
                    </Box>
                    <Menu
                      open={open}
                      anchorEl={anchorEl}
                      onClose={closePopover}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      sx={{
                        mt: 1,
                        '& .MuiPaper-root': {
                          boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                        },
                      }}
                    >
                      {boardsFilterDropdown?.map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => {
                            handleItemTypeChange(option.value);
                            closePopover();
                          }}
                          sx={{
                            padding: '8px 16px',
                            fontSize: '14px',
                            minWidth: '140px',
                            '&.Mui-selected': {
                              backgroundColor: '#f0f0f0',
                            },
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                          selected={option.value === filter.selectedItemType}
                        >
                          <Typography
                            sx={{
                              fontWeight:
                                option.value === filter.selectedItemType
                                  ? 500
                                  : 400,
                            }}
                          >
                            {option.label}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </Box>
              }
            />
          </Grid>
        </Box>
      </Grid>

      {isBoardsLoading || isBoardsFetching ? (
        <BoardListViewSkeleton />
      ) : boardsData && boardsData.data.length === 0 ? (
        <Grid container alignItems='center' gap={2} minHeight={100}>
          <Typography fontFamily='Mont-Semibold' variant='h6' marginLeft={0.5}>
            0 results
          </Typography>
          <InfoCard
            info='No Boards found'
            type='error'
            cardStyles={{ width: '100%' }}
          />
        </Grid>
      ) : (
        <>
          <Grid item alignItems='center' xs={12}>
            {!!filter.search?.length && (
              <Typography
                fontFamily='Mont-Semibold'
                variant='h6'
                sx={{
                  marginLeft: 0.5,
                  marginTop: 0,
                  marginBottom: 2,
                  fontSize: '18px',
                }}
              >
                {totalBoards} results
              </Typography>
            )}
            <Box
              sx={{
                mb: 8,
                maxHeight: 500,
                mt: 0,
                backgroundColor: '#F9F9F9',
                boxShadow: 'none',
              }}
            >
              <BoardsListView
                showPaginationControls={true}
                currentPagination={currentPagination}
                setCurrentPagination={setCurrentPagination}
                boards={boardsData}
                total={totalBoards}
              />
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default BoardsScreen;
